import { useState, useRef, useEffect } from 'react'
import SockJS from 'sockjs-client'
import { Client } from '@stomp/stompjs'
import { uploadDemandFile, uploadSupplyFile, uploadMsaFile, uploadBosFile, uploadNationalFile, uploadStateFile, getDemandRecords, getSupplyRecords, getMsaRecords, getBosRecords, getNationalRecords, getStateRecords } from '../../services/datasetService.jsx'
import DataReviewModal from '../../components/modals/DataReviewModal'
import { FaDatabase } from 'react-icons/fa6'
import { DatabaseBackup, Loader2 } from 'lucide-react'
import { toast } from 'react-toastify'
import './styles.scss'

const YEAR = '2025'

// ── Reusable upload card ──────────────────────────────────────────────────────
function UploadCard({ type, label, accept, icon, onSelected, onReset, onViewExisting, status }) {
  const inputRef = useRef(null)
  const [fileName, setFileName] = useState(null)
  const [dragging, setDragging] = useState(false)

  function handleFile(file) {
    if (!file) return
    setFileName(file.name)
    onSelected(file)
  }

  function doReset(e) {
    e.stopPropagation()
    setFileName(null)
    onReset()
  }

  function onDrop(e) {
    e.preventDefault()
    setDragging(false)
    handleFile(e.dataTransfer.files[0])
  }

  return (
    <div className={`upload-card ${status === 'loading' ? 'card--loading' : ''}`}>
      <div className="upload-card-header">
        <span className="upload-icon">{icon}</span>
        <div className="upload-meta-wrapper">
          <h2 className="upload-title">{label}</h2>
          <p className="upload-meta">{type === 'demand' ? 'CSV  •  Tab-separated' : 'XLSX  •  Excel Workbook'}</p>
        </div>
        <button
          className="btn-preview-existing"
          onClick={(e) => { e.stopPropagation(); onViewExisting(); }}
          title="Audit existing records in database"
        >
          <FaDatabase />
        </button>
        {fileName && (
          <button className="btn-reset" onClick={doReset} title="Clear selection">
            ✕
          </button>
        )}
      </div>

      <div
        className={`dropzone ${dragging ? 'dropzone--active' : ''} ${fileName ? 'dropzone--filled' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={(e) => handleFile(e.target.files[0])}
        />
        {fileName
          ? (
            <div className="file-info pulse">
              <span className="dropzone-file-icon">📄</span>
              <span className="dropzone-filename">{fileName}</span>
            </div>
          )
          : (
            <div className="upload-prompt">
              <span className="dropzone-hint-icon">🚀</span>
              <span className="dropzone-hint">Drag & drop or Click to upload</span>
            </div>
          )
        }
      </div>
    </div>
  )
}

function ProgressBar({ progress }) {
  if (!progress) return null;
  return (
    <div className="progress-container animate-fade-in" style={{ marginTop: '1.5rem' }}>
      <div className="progress-stats">
        <span className="progress-percent">{progress.percent}%</span>
        {progress.total > 0 ? (
          <span className="progress-count">{progress.processed.toLocaleString()} / {progress.total.toLocaleString()} records</span>
        ) : (
          <span className="progress-count">{progress.processed.toLocaleString()} records processed</span>
        )}
      </div>
      <div className="progress-bar-bg">
        <div
          className="progress-bar-fill pulse"
          style={{ width: `${Math.min(100, Math.max(0, progress.percent))}%` }}
        ></div>
      </div>
      {progress.percent < 100 && (
        <div className="progress-hint">
          <Loader2 className="spinning-icon" size={14} />
          Keep this window open to watch real-time sync
        </div>
      )}
    </div>
  );
}

export default function Datasets() {
  const [demandFile, setDemandFile] = useState(null)
  const [supplyFile, setSupplyFile] = useState(null)
  const [msaFile, setMsaFile] = useState(null)
  const [bosFile, setBosFile] = useState(null)
  const [nationalFile, setNationalFile] = useState(null)
  const [stateFile, setStateFile] = useState(null)
  
  const [demandStatus, setDemandStatus] = useState(null)
  const [supplyStatus, setSupplyStatus] = useState(null)
  const [stateStatus, setStateStatus] = useState(null)
  const [msaStatus, setMsaStatus] = useState(null)
  const [bosStatus, setBosStatus] = useState(null)
  const [nationalStatus, setNationalStatus] = useState(null)
  
  const [demandProgress, setDemandProgress] = useState(null)
  const [supplyProgress, setSupplyProgress] = useState(null)
  const [stateProgress, setStateProgress] = useState(null)
  const [msaProgress, setMsaProgress] = useState(null)
  const [bosProgress, setBosProgress] = useState(null)
  const [nationalProgress, setNationalProgress] = useState(null)
  
  const stompClient = useRef(null)

  // -- WebSocket Setup --
  useEffect(() => {
    const WS_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL || 'https://localhost:7777/r/polytechnic-app'
    const socket = new SockJS(`${WS_BASE_URL}/polytechnic-ws`)
    const client = new Client({
      webSocketFactory: () => socket,
      debug: (msg) => console.log('WebSocket Debug:', msg),
      onConnect: () => {
        console.log('✅ Connected to WebSocket Pulse')
        
        const handleProgress = (message, setProgress, setStatus, name) => {
            const data = JSON.parse(message.body)
            setProgress(data)
            if (data.percent >= 100) {
              setStatus({ success: true, imported: data.processed, isBackground: true })
              toast.success(`${name} Ingestion Complete! ${data.processed} records synced.`)
              setTimeout(() => setProgress(null), 5000)
            }
        }

        client.subscribe('/topic/demand-progress', (message) => handleProgress(message, setDemandProgress, setDemandStatus, 'Demand'))
        client.subscribe('/topic/supply-progress', (message) => handleProgress(message, setSupplyProgress, setSupplyStatus, 'Supply'))
        client.subscribe('/topic/msa-progress', (message) => handleProgress(message, setMsaProgress, setMsaStatus, 'MSA'))
        client.subscribe('/topic/bos-progress', (message) => handleProgress(message, setBosProgress, setBosStatus, 'BOS OEWS'))
        client.subscribe('/topic/national-progress', (message) => handleProgress(message, setNationalProgress, setNationalStatus, 'National NAICS'))
        client.subscribe('/topic/state-progress', (message) => handleProgress(message, setStateProgress, setStateStatus, 'State OEWS'))
      },
      onStompError: (frame) => {
        console.error('❌ STOMP error', frame.headers['message'])
      }
    })

    client.activate()
    stompClient.current = client

    return () => {
      if (stompClient.current) {
        stompClient.current.deactivate()
      }
    }
  }, [])

  // -- Audit Modal States --
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [modalData, setModalData] = useState([])

  async function handleViewExisting(type) {
    try {
      let title = '';
      let records = [];

      if (type === 'demand') {
        title = 'Existing Demand Catalog';
        records = await getDemandRecords();
      } else if (type === 'supply') {
        title = 'Existing Supply Catalog';
        records = await getSupplyRecords();
      } else if (type === 'msa') {
        title = 'Regional MSA Occupation Registry (2024)';
        records = await getMsaRecords();
      } else if (type === 'bos') {
        title = 'BOS National OEWS Registry (2024)';
        records = await getBosRecords();
      } else if (type === 'national') {
        title = 'National NAICS Module Registry (2024)';
        records = await getNationalRecords();
      } else if (type === 'state') {
        title = 'State OEWS Module Registry (2024)';
        records = await getStateRecords();
      }

      setModalTitle(title)
      if (!records || !records.length) {
        toast.info(`No existing ${type} records found in the Memphis regional database.`)
        return
      }

      setModalData(records)
      setIsReviewModalOpen(true)
    } catch (e) {
      toast.error(`Audit Failed: ${e.message}`)
    }
  }

  async function handleImport(file, uploadFn, setStatus, setProgress, name) {
    if (!file) return
    setStatus('loading')
    setProgress({ percent: 0, processed: 0, total: 0 })
    try {
      const result = await uploadFn(file)
      setStatus({ success: true, message: result.message, isBackground: true })
      toast.info(`📁 ${name} file accepted. Streaming ingestion starting in background...`)
    } catch (e) {
      setStatus({ error: e.message })
      setProgress(null)
    }
  }

  return (
    <div className="dashboard-container datasets-page-context">
      <header className="page-header-premium">
        <div className="hero-section">
          <DatabaseBackup className="header-icon-large" />
          <div className="title-stack">
            <div className="hero-badge">{YEAR} SESSION ACTIVE</div>
            <h1 className="dashboard-title">
              Data Ingestion <br />
              <span className="text-gradient">Center for PolyTechnic</span>
            </h1>
            <p className="subtitle">
              Securely stream multi-year workforce data. <br />
              Records with matching unique identifiers will be updated automatically via background service.
            </p>
          </div>
        </div>
      </header>

      <main className="analytics-grid">
        <div className="analytics-card dataset-section">
          <UploadCard
            type="demand"
            label="Demand Dataset"
            accept=".csv"
            icon="📈"
            onSelected={setDemandFile}
            onReset={() => { setDemandFile(null); setDemandStatus(null) }}
            onViewExisting={() => handleViewExisting('demand')}
            status={demandStatus}
          />
          <div className="import-action-bar">
            <button
              className={`btn-import ${demandStatus === 'loading' ? 'is-loading' : ''}`}
              disabled={!demandFile || demandStatus === 'loading'}
              onClick={() => handleImport(demandFile, uploadDemandFile, setDemandStatus, setDemandProgress, 'Demand')}
            >
              {demandStatus === 'loading' ? 'Uploading File...' : `Launch Background Ingestion`}
            </button>
            {demandStatus && demandStatus !== 'loading' && (
              <div className={`status-pill ${demandStatus.success ? 'pill-success' : 'pill-error'}`}>
                {demandStatus.success ? '✓ Processing on server' : `✕ ${demandStatus.error}`}
              </div>
            )}
          </div>
          <ProgressBar progress={demandProgress} />
        </div>

        <div className="analytics-card dataset-section">
          <UploadCard
            type="supply"
            label="Supply Dataset"
            accept=".xlsx"
            icon="⚙️"
            onSelected={setSupplyFile}
            onReset={() => { setSupplyFile(null); setSupplyStatus(null) }}
            onViewExisting={() => handleViewExisting('supply')}
            status={supplyStatus}
          />
          <div className="import-action-bar">
            <button
              className={`btn-import ${supplyStatus === 'loading' ? 'is-loading' : ''}`}
              disabled={!supplyFile || supplyStatus === 'loading'}
              onClick={() => handleImport(supplyFile, uploadSupplyFile, setSupplyStatus, setSupplyProgress, 'Supply')}
            >
              {supplyStatus === 'loading' ? 'Uploading File...' : `Launch Background Ingestion`}
            </button>
            {supplyStatus && supplyStatus !== 'loading' && (
              <div className={`status-pill ${supplyStatus.success ? 'pill-success' : 'pill-error'}`}>
                {supplyStatus.success ? '✓ Processing on server' : `✕ ${supplyStatus.error}`}
              </div>
            )}
          </div>
          <ProgressBar progress={supplyProgress} />
        </div>

        <div className="analytics-card dataset-section">
          <UploadCard
            type="msa"
            label="Regional MSA Occupations"
            accept=".xlsx"
            icon="🏢"
            onSelected={setMsaFile}
            onReset={() => { setMsaFile(null); setMsaStatus(null) }}
            onViewExisting={() => handleViewExisting('msa')}
            status={msaStatus}
          />
          <div className="import-action-bar">
            <button
              className={`btn-import ${msaStatus === 'loading' ? 'is-loading' : ''}`}
              disabled={!msaFile || msaStatus === 'loading'}
              onClick={() => handleImport(msaFile, uploadMsaFile, setMsaStatus, setMsaProgress, 'MSA')}
            >
              {msaStatus === 'loading' ? 'Uploading File...' : `Launch Background Ingestion`}
            </button>
            {msaStatus && msaStatus !== 'loading' && (
              <div className={`status-pill ${msaStatus.success ? 'pill-success' : 'pill-error'}`}>
                {msaStatus.success ? '✓ Processing on server' : `✕ ${msaStatus.error}`}
              </div>
            )}
          </div>
          <ProgressBar progress={msaProgress} />
        </div>

        <div className="analytics-card dataset-section">
          <UploadCard
            type="bos"
            label="BOS National OEWS (2024)"
            accept=".xlsx"
            icon="🌐"
            onSelected={setBosFile}
            onReset={() => { setBosFile(null); setBosStatus(null) }}
            onViewExisting={() => handleViewExisting('bos')}
            status={bosStatus}
          />
          <div className="import-action-bar">
            <button
              className={`btn-import ${bosStatus === 'loading' ? 'is-loading' : ''}`}
              disabled={!bosFile || bosStatus === 'loading'}
              onClick={() => handleImport(bosFile, uploadBosFile, setBosStatus, setBosProgress, 'BOS')}
            >
              {bosStatus === 'loading' ? 'Uploading File...' : `Launch Background Ingestion`}
            </button>
            {bosStatus && bosStatus !== 'loading' && (
              <div className={`status-pill ${bosStatus.success ? 'pill-success' : 'pill-error'}`}>
                {bosStatus.success ? '✓ Processing on server' : `✕ ${bosStatus.error}`}
              </div>
            )}
          </div>
          <ProgressBar progress={bosProgress} />
        </div>

        <div className="analytics-card dataset-section">
          <UploadCard
            type="national"
            label="National NAICS Module (2024)"
            accept=".xlsx"
            icon="🇺🇸"
            onSelected={setNationalFile}
            onReset={() => { setNationalFile(null); setNationalStatus(null) }}
            onViewExisting={() => handleViewExisting('national')}
            status={nationalStatus}
          />
          <div className="import-action-bar">
            <button
              className={`btn-import ${nationalStatus === 'loading' ? 'is-loading' : ''}`}
              disabled={!nationalFile || nationalStatus === 'loading'}
              onClick={() => handleImport(nationalFile, uploadNationalFile, setNationalStatus, setNationalProgress, 'National')}
            >
              {nationalStatus === 'loading' ? 'Uploading File...' : `Launch Background Ingestion`}
            </button>
            {nationalStatus && nationalStatus !== 'loading' && (
              <div className={`status-pill ${nationalStatus.success ? 'pill-success' : 'pill-error'}`}>
                {nationalStatus.success ? '✓ Processing on server' : `✕ ${nationalStatus.error}`}
              </div>
            )}
          </div>
          <ProgressBar progress={nationalProgress} />
        </div>

        <div className="analytics-card dataset-section">
          <UploadCard
            type="state"
            label="State OEWS Module (2024)"
            accept=".xlsx"
            icon="🗺️"
            onSelected={setStateFile}
            onReset={() => { setStateFile(null); setStateStatus(null) }}
            onViewExisting={() => handleViewExisting('state')}
            status={stateStatus}
          />
          <div className="import-action-bar">
            <button
              className={`btn-import ${stateStatus === 'loading' ? 'is-loading' : ''}`}
              disabled={!stateFile || stateStatus === 'loading'}
              onClick={() => handleImport(stateFile, uploadStateFile, setStateStatus, setStateProgress, 'State')}
            >
              {stateStatus === 'loading' ? 'Uploading File...' : `Launch Background Ingestion`}
            </button>
            {stateStatus && stateStatus !== 'loading' && (
              <div className={`status-pill ${stateStatus.success ? 'pill-success' : 'pill-error'}`}>
                {stateStatus.success ? '✓ Processing on server' : `✕ ${stateStatus.error}`}
              </div>
            )}
          </div>
          <ProgressBar progress={stateProgress} />
        </div>
      </main>

      {isReviewModalOpen && (
        <DataReviewModal
          label={modalTitle}
          rows={modalData}
          onClose={() => setIsReviewModalOpen(false)}
        />
      )}
    </div>
  )
}
